<template>
	<div class="page datasources-page">
		<div class="content-sections">
			<!-- DATA DRIVES -->
			<section class="stack">
				<div class="section-title section-title--baseline">
					<div class="sgnl-h6-semibold">Data Drives</div>
					<QuotaCount
						:used="driveList.length"
						limit-id="DataDriveCount"
					/>
					<v-tooltip
						bottom
						max-width="500px"
					>
						<template v-slot:activator="{ props }">
							<Information16
								class="section-title-info"
								v-bind="props"
							/>
						</template>
						Data Drives are collections of data sources with associated mount points. They allow you to
						mount multiple data sources for the same execution so that applications running on Signaloid
						Cloud can access their data.
					</v-tooltip>
				</div>
				<ErrorEmptyState
					v-if="dataSourcesStore.driveFetchStatus.error && driveList.length === 0"
					title="Could not load Data Drives"
					message="Often this is a temporary network or connectivity issue."
					:retrying="dataSourcesStore.driveFetchStatus.loading"
					bordered
					@retry="retryFetchDrives"
				/>
				<div
					v-else
					class="card-grid"
				>
					<DriveCard
						v-for="driveID in driveList"
						:key="driveID"
						:driveID="driveID"
						@drive-disconnected="driveDisconnectionHandler"
						@drive-disconnection-failed="driveDisconnectionFailedHandler"
						@drive-config-updated="driveConfigUpdatedHandler"
					/>
					<div
						class="placeholder-content new-source-dims"
						data-test="data-drive-add-button"
					>
						<div
							class="new-data-source-icon new-data-source-icon--drive"
							role="img"
							aria-label="New Data Drive"
						/>
						<template v-if="dataDriveLimitReached">
							<div class="new-data-source-card-text">
								<div class="sgnl-paragraph-s-medium sgnl-text-color-default">
									Data Drive limit reached
								</div>
								<div class="sgnl-paragraph-s-regular sgnl-text-color-secondary">
									{{ dataDriveUsageLabel }}
								</div>
							</div>
							<v-btn
								variant="flat"
								color="primary"
								:to="{ path: '/billing' }"
							>
								<span>Upgrade</span>
								<Upgrade16 />
							</v-btn>
						</template>
						<template v-else>
							<div class="new-data-source-card-text sgnl-paragraph-s-regular sgnl-text-color-secondary">
								Create new Data Drive
							</div>
							<v-btn
								variant="flat"
								color="primary"
								@click="addDrive"
							>
								<Add16 />
								<span>New Data Drive</span>
							</v-btn>
						</template>
					</div>
				</div>
			</section>

			<section class="stack">
				<!-- CONNECTED SENSOR GATEWAYS -->
				<div class="section-title section-title--baseline">
					<div class="sgnl-h6-semibold">Connected Sensor Gateways</div>
					<QuotaCount
						:used="gatewayList.length"
						limit-id="GatewayCount"
					/>
					<v-tooltip
						bottom
						max-width="500px"
					>
						<template v-slot:activator="{ props }">
							<Information16
								class="section-title-info"
								v-bind="props"
							/>
						</template>
						<em>Signaloid Sensor Gateways</em> connect to different sensor modules and stream data from them
						to Signaloid Cloud. <em>Signaloid Sensor Gateways</em> connected to your account will appear
						here once you set them up. Please refer to the initial configuration instructions you received
						with the gateway.
					</v-tooltip>
				</div>
				<ErrorEmptyState
					v-if="dataSourcesStore.gatewayFetchStatus.error && gatewayList.length === 0"
					title="Could not load Sensor Gateways"
					message="Often this is a temporary network or connectivity issue."
					:retrying="dataSourcesStore.gatewayFetchStatus.loading"
					bordered
					@retry="retryFetchGateways"
				/>
				<div
					v-else
					class="card-grid"
				>
					<GatewayCard
						v-for="gateway in gatewayList"
						:key="gateway"
						:gatewayID="gateway"
					/>
					<SourcePlaceholderCard
						v-if="gatewayList.length === 0"
						min-height="250px"
						:disabled="true"
						:loading="dataSourcesStore.gatewayFetchStatus.loading"
					>
						<template #icon>
							<span
								class="data-source-icon data-source-icon--gateway data-source-icon--32"
								role="img"
								aria-label="Sensor Gateway"
							/>
						</template>
						<template #buttonText
							><span class="sgnl-paragraph-s-regular sgnl-text-color-secondary"
								>No Sensor Gateways available</span
							></template
						>
					</SourcePlaceholderCard>
				</div>
			</section>

			<section class="stack">
				<!-- DATA SOURCES -->
				<div class="section-title section-title--baseline">
					<div class="sgnl-h6-semibold">Data Sources</div>
					<QuotaCount
						:used="dataSourceList.length"
						limit-id="DataSourceCount"
					/>
					<v-tooltip
						bottom
						max-width="500px"
					>
						<template v-slot:activator="{ props }">
							<Information16
								class="section-title-info"
								v-bind="props"
							/>
						</template>
						Adding a data source will allow your applications to have access to persistent storage to read
						and write from.
					</v-tooltip>
				</div>
				<ErrorEmptyState
					v-if="dataSourcesStore.bucketFetchStatus.error && dataSourceList.length === 0"
					title="Could not load Data Sources"
					message="Often this is a temporary network or connectivity issue."
					:retrying="dataSourcesStore.bucketFetchStatus.loading"
					bordered
					@retry="retryFetchBuckets"
				/>
				<div
					v-else
					class="card-grid"
				>
					<SigCloudStorageCard @storage-operation-successful="storageOperationHandler" />
					<BucketCard
						v-for="dataSourceID in dataSourceList"
						:key="dataSourceID"
						:bucketID="dataSourceID"
						@bucket-disconnected="bucketDisconnectionHandler"
						@bucket-disconnection-failed="bucketDisconnectionFailedHandler"
						@bucket-config-updated="bucketConfigUpdatedHandler"
					/>
					<div class="placeholder-content new-source-dims">
						<img
							src="@/assets/icons/data-sources/new-data-source.svg"
							width="48"
							height="48"
							alt="New Data Source"
						/>
						<template v-if="dataSourceLimitReached">
							<div class="new-data-source-card-text">
								<div class="sgnl-paragraph-s-medium sgnl-text-color-default">
									Data Source limit reached
								</div>
								<div class="sgnl-paragraph-s-regular sgnl-text-color-secondary">
									{{ dataSourceUsageLabel }}
								</div>
							</div>
							<v-btn
								variant="flat"
								color="primary"
								:to="{ path: '/billing' }"
							>
								<span>Upgrade</span>
								<Upgrade16 />
							</v-btn>
						</template>
						<template v-else>
							<div class="new-data-source-card-text sgnl-paragraph-s-regular sgnl-text-color-secondary">
								Create new Data Source
							</div>
							<v-btn
								variant="flat"
								color="primary"
								@click="addDataSource"
							>
								<Add16 />
								<span>New Data Source</span>
							</v-btn>
						</template>
					</div>
				</div>
			</section>
		</div>

		<DataSourceConfigDialog
			v-model:showDialog="showDataSourceConfigDialog"
			@bucket-connected="bucketConnectionHandler"
		/>
		<DriveConfigDialog
			v-if="showDriveConfigDialog"
			v-model:showDialog="showDriveConfigDialog"
			@drive-config-updated="driveConfigUpdatedFromDialogHandler"
			@drive-created="driveCreationHandler"
		/>
		<v-snackbar
			v-model="successSnackbar"
			:timeout="4000"
			class="snackbar-success"
		>
			<div class="snackbar-icon-container snackbar-success">
				<CheckmarkFilled16 />
			</div>
			{{ successMessage }}
			<template v-slot:actions>
				<v-btn
					variant="text"
					size="small"
					icon
					@click="successSnackbar = false"
				>
					<Close16 class="sgnl-icon-color-secondary" />
				</v-btn>
			</template>
		</v-snackbar>
		<v-snackbar
			v-model="errorSnackbar"
			:timeout="6000"
			class="snackbar-error"
		>
			<div class="snackbar-icon-container snackbar-error">
				<ErrorFilled16 />
			</div>
			{{ errorMessage }}
			<template v-slot:actions>
				<v-btn
					variant="text"
					size="small"
					icon
					@click="errorSnackbar = false"
				>
					<Close16 class="sgnl-icon-color-secondary" />
				</v-btn>
			</template>
		</v-snackbar>
	</div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Information16 from "@carbon/icons-vue/es/information/16";
import Add16 from "@carbon/icons-vue/es/add/16";
import Upgrade16 from "@carbon/icons-vue/es/upgrade/16";
import CheckmarkFilled16 from "@carbon/icons-vue/es/checkmark--filled/16";
import Close16 from "@carbon/icons-vue/es/close/16";
import ErrorFilled16 from "@carbon/icons-vue/es/error--filled/16";

// Types
import { DataSourceType, DataSourceTypeE } from "@/types/api/dataSources";

// Libraries

// Utilities
import * as dsUtil from "@/components/DataSources/utilities";

// Components
import GatewayCard from "@/components/DataSources/GatewayCard.vue";
import BucketCard from "@/components/DataSources/BucketCard.vue";
import DriveCard from "@/components/DataSources/DriveCard.vue";
import DataSourceConfigDialog from "@/components/DataSources/DataSourceConfigDialog.vue";
import DriveConfigDialog from "@/components/DataSources/DriveConfigDialog.vue";
import SigCloudStorageCard from "@/components/DataSources/SigCloudStorageCard.vue";
import SourcePlaceholderCard from "@/components/DataSources/SourcePlaceholderCard.vue";
import ErrorEmptyState from "@/components/Common/ErrorEmptyState.vue";
import QuotaCount from "@/components/Common/QuotaCount.vue";

// Stores
import { mapState } from "pinia";
import { useDataSourcesStore } from "@/stores/dataSources";
import { useUserStore } from "@/stores/user";
import { UserLimitsE, withinLimit } from "@/js/tierconfig";
import { TierLimitEventTypeE } from "@/eventBus/tierLimitEventBus";
import { useRootStore } from "@/stores/root";

// Global variables

type ComponentData = {
	showDataSourceConfigDialog: boolean;
	showDriveConfigDialog: boolean;
	successSnackbar: boolean;
	successMessage: string;
	errorSnackbar: boolean;
	errorMessage: string;
};

export default defineComponent({
	name: "DataSources",
	components: {
		GatewayCard,
		BucketCard,
		SigCloudStorageCard,
		DataSourceConfigDialog,
		DriveCard,
		DriveConfigDialog,
		SourcePlaceholderCard,
		ErrorEmptyState,
		QuotaCount,
		Information16,
		Add16,
		Upgrade16,
		CheckmarkFilled16,
		Close16,
		ErrorFilled16,
	},
	data: (): ComponentData => ({
		showDataSourceConfigDialog: false,
		showDriveConfigDialog: false,
		successSnackbar: false,
		successMessage: "",
		errorSnackbar: false,
		errorMessage: "",
	}),
	setup() {
		// Note: gets called after beforeCreate in vue2
		const userStore = useUserStore();
		const dataSourcesStore = useDataSourcesStore();
		const rootStore = useRootStore();
		return { dataSourcesStore, DataSourceTypeE, userStore, rootStore, UserLimitsE };
	},
	methods: {
		addDataSource() {
			this.showDataSourceConfigDialog = true;
		},
		addDrive() {
			this.showDriveConfigDialog = true;
		},
		// Handle drive events
		async driveCreationHandler() {
			this.rootStore.tierLimitEventBus.emit({
				type: TierLimitEventTypeE.UsageChanged,
				affectedLimits: [UserLimitsE.DataDriveCount],
			});
			this.fetchDriveList();
			this.successMessage = "Data drive created successfully.";
			this.successSnackbar = true;
		},
		async driveDisconnectionHandler() {
			this.rootStore.tierLimitEventBus.emit({
				type: TierLimitEventTypeE.UsageChanged,
				affectedLimits: [UserLimitsE.DataDriveCount],
			});
			this.fetchDriveList();
			this.successMessage = "Data drive disconnected successfully.";
			this.successSnackbar = true;
		},
		driveDisconnectionFailedHandler() {
			this.errorMessage = "Failed to disconnect data drive. Please try again.";
			this.errorSnackbar = true;
		},
		driveConfigUpdatedHandler() {
			this.successMessage = "Data drive configuration updated successfully.";
			this.successSnackbar = true;
		},
		driveConfigUpdatedFromDialogHandler() {
			this.fetchDriveList();
			this.successMessage = "Data drive configuration updated successfully.";
			this.successSnackbar = true;
		},
		async fetchDriveList() {
			await this.dataSourcesStore.fetchDriveList();
		},
		// Handle bucket events
		async bucketDisconnectionHandler() {
			this.rootStore.tierLimitEventBus.emit({
				type: TierLimitEventTypeE.UsageChanged,
				affectedLimits: [UserLimitsE.BucketCount],
			});
			this.fetchBucketList();
			this.successMessage = "Data source disconnected successfully.";
			this.successSnackbar = true;
		},
		bucketDisconnectionFailedHandler() {
			this.errorMessage = "Failed to disconnect data source. Please try again.";
			this.errorSnackbar = true;
		},
		bucketConfigUpdatedHandler() {
			this.successMessage = "Data source configuration updated successfully.";
			this.successSnackbar = true;
		},
		async bucketConnectionHandler() {
			this.rootStore.tierLimitEventBus.emit({
				type: TierLimitEventTypeE.UsageChanged,
				affectedLimits: [UserLimitsE.BucketCount],
			});
			this.fetchBucketList();
			this.successMessage = "Data source connected successfully.";
			this.successSnackbar = true;
		},
		// Handle storage events
		storageOperationHandler(message: string) {
			this.successMessage = message;
			this.successSnackbar = true;
		},
		async fetchBucketList() {
			await this.dataSourcesStore.fetchBucketList();
		},
		dataSourceIcon(dataSourceType: DataSourceType) {
			return dsUtil.dataSourceIcon(dataSourceType);
		},
		async retryFetchDrives() {
			await this.dataSourcesStore.fetchDriveList();
		},
		async retryFetchGateways() {
			await this.dataSourcesStore.fetchGatewayList();
		},
		async retryFetchBuckets() {
			await this.dataSourcesStore.fetchBucketList();
		},
	},
	computed: {
		dataSourceList(): string[] {
			return [...this.bucketList];
		},
		...mapState(useDataSourcesStore, {
			driveList: "driveList",
			bucketList: "bucketList",
			gatewayList: "gatewayList",
		}),
		dataSourceLimitReached(): boolean {
			const currentUserTier = this.userStore.currentUserTierDetails;
			if (!currentUserTier) {
				return false;
			}

			const dataSourceCountAfterCreation = this.dataSourceList.length + 1;
			return !withinLimit(dataSourceCountAfterCreation, currentUserTier.allowance.DataSourceCount);
		},
		dataDriveLimitReached(): boolean {
			const currentUserTier = this.userStore.currentUserTierDetails;
			if (!currentUserTier) {
				return false;
			}

			const dataDriveCountAfterCreation = this.driveList.length + 1;
			return !withinLimit(dataDriveCountAfterCreation, currentUserTier.allowance.DataDriveCount);
		},
		dataDriveUsageLabel(): string {
			const limit = this.userStore.currentUserTierDetails?.allowance.DataDriveCount;
			if (limit === null) {
				return "Data Drives are not included in your tier";
			}
			if (limit === undefined) {
				return "";
			}
			return `${this.driveList.length} of ${limit} Data Drive${limit === 1 ? "" : "s"} in use`;
		},
		dataSourceUsageLabel(): string {
			const limit = this.userStore.currentUserTierDetails?.allowance.DataSourceCount;
			if (limit === null) {
				return "Data Sources are not included in your tier";
			}
			if (limit === undefined) {
				return "";
			}
			return `${this.dataSourceList.length} of ${limit} Data Source${limit === 1 ? "" : "s"} in use`;
		},
	},
	created() {
		this.userStore.getCurrentUserTierDetails();
		const pending: Promise<unknown>[] = [];
		if (this.gatewayList.length == 0) pending.push(this.dataSourcesStore.fetchGatewayList());
		if (this.driveList.length == 0) pending.push(this.dataSourcesStore.fetchDriveList());
		if (this.bucketList.length == 0) pending.push(this.dataSourcesStore.fetchBucketList());
		void Promise.all(pending);
	},
});
</script>

<style lang="scss" scoped>
.card-grid {
	display: grid;
	gap: var(--sgnl-space-16);
	grid-template-columns: 1fr;
	min-height: 250px;
}

@media (min-width: map-get($grid-breakpoints, sm)) {
	.card-grid {
		grid-template-columns: repeat(auto-fill, minmax(500px, 1fr));
	}
}

/* Uniform card height across the grid so no shift between loading/loaded states. */
.card-grid > * {
	min-height: 250px;
}

.new-source-dims {
	min-width: 285px;
	min-height: 250px;
}

.new-data-source-icon {
	width: var(--sgnl-icon-48);
	height: var(--sgnl-icon-48);
	background-color: var(--sgnl-default-tertiary-icon);
	-webkit-mask-size: contain;
	mask-size: contain;
	-webkit-mask-repeat: no-repeat;
	mask-repeat: no-repeat;
	-webkit-mask-position: center;
	mask-position: center;
}

.new-data-source-icon--drive {
	-webkit-mask-image: url("@/assets/icons/data-sources/new-data-drive.svg");
	mask-image: url("@/assets/icons/data-sources/new-data-drive.svg");
}

.new-data-source-card-text {
	text-align: center;
}
</style>

<style lang="scss">
@media (max-width: 599px) {
	.datasources-page .card-grid .sgnl-flex:has(.v-btn) {
		flex-direction: column;
	}

	.datasources-page .card-grid .sgnl-flex:has(.v-btn) .v-spacer {
		display: none;
	}
}
</style>
